import React, { useEffect, useState, useRef, useContext } from 'react'
import ModalTest from '../../BaseModal'
import * as S from '../../styles/modal-style'
import { Ellipsis, Plus, Trash, X } from 'lucide-react'
import axios from '../../../config/axios'
import { AddInStep } from '../../../pages/ClientPanel/Pipelines/style'
import { FeedbackContext } from '../../../contexts/FeedbackContext'
import { getTagTextColor } from '../../../utils/colorContrast'

interface PropsInterface {
  collectionId: number
  closeModal: () => void
  onSave?: (themes: any[]) => void
}

const DEFAULT_HEX_COLOR = '#99D3EE'

const normalizeHexColor = (value: string): string | null => {
  const sanitizedValue = String(value || '').trim().replace('#', '')
  if (/^[0-9a-fA-F]{3}$/.test(sanitizedValue)) {
    const [r, g, b] = sanitizedValue.split('')
    return `#${`${r}${r}${g}${g}${b}${b}`.toUpperCase()}`
  }
  if (/^[0-9a-fA-F]{6}$/.test(sanitizedValue)) {
    return `#${sanitizedValue.toUpperCase()}`
  }
  return null
}

const getHexColorOrDefault = (value?: string | null): string => {
  return normalizeHexColor(String(value || '')) || DEFAULT_HEX_COLOR
}

const ColorDiv: React.FC<{ color: string }> = ({ color }) => {
  return <div style={{ width: '22px', height: '22px', backgroundColor: color, borderRadius: '50%' }} />
}

const ViewOrEditThemes: React.FC<PropsInterface> = ({ collectionId, closeModal, onSave }) => {
  const { setFeedback } = useContext(FeedbackContext)
  const [themes, setThemes] = useState<any[]>([])
  const [dropDownIndex, setDropDownIndex] = useState<number | null>(null)
  const [dropdownCoords, setDropdownCoords] = useState<{ top: number, left: number, width: number } | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<(HTMLDivElement | null)[]>([])
  const [newNameInput, setNewNameInput] = useState<string>('')
  const [newColorInput, setNewColorInput] = useState<string>(DEFAULT_HEX_COLOR)

  useEffect(() => {
    const fetchThemes = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/themes`)
        if (response.data) {
          const rawThemes = Array.isArray(response.data) ? response.data : []
          const normalizedThemes = rawThemes.map((theme: any) => ({
            ...theme,
            color: getHexColorOrDefault(theme.color),
          }))
          setThemes(normalizedThemes)
        }
      } catch (error: any) {
        console.error('[ViewOrEditThemes] erro fetchThemes', error)
        setFeedback({
          isOpen: true,
          statusOk: false,
          message:
            error?.response?.data?.message ||
            'Erro ao carregar temas. Verifique a API do backend-fashion-orders.',
        })
      }
    }
    fetchThemes()
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement
      const isOutside = dropdownRef.current && !dropdownRef.current.contains(targetElement)
      console.debug('[DEBUG] handleClickOutside', { dropDownIndex, isOutside, targetTag: targetElement?.tagName, targetClass: targetElement?.className })

      if (isOutside && dropDownIndex !== null) {
        console.debug('[DEBUG] click fora - salvando tema', { index: dropDownIndex, value: newNameInput })
        handleSaveEditedTheme(dropDownIndex)
        setDropDownIndex(null)
      }
    }

    if (dropDownIndex !== null) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropDownIndex, newNameInput])

  const handleIsEditingTheme = (index: number) => {
    const theme = themes[index]
    if (!theme) return

    if (theme.inUse) {
      setFeedback({
        isOpen: true,
        statusOk: true,
        message: 'Tema em uso: somente cor pode ser alterada.',
      })
    }

    const rowElement = rowRefs.current[index]
    console.debug('[DEBUG] handleIsEditingTheme', { index, rowElement, currentName: theme.name, inUse: theme.inUse })
    if (rowElement) {
      const rect = rowElement.getBoundingClientRect()
      const width = 280
      const left = Math.max(16, rect.right - width)
      setDropdownCoords({ top: rect.bottom + 6, left, width })
    }
    setDropDownIndex(index)
    setNewNameInput(theme.name)
    setNewColorInput(getHexColorOrDefault(theme.color))
  }

  const handleSaveEditedTheme = (index: number | null = dropDownIndex) => {
    console.debug('[DEBUG] handleSaveEditedTheme chamado', { index, newNameInput })
    if (index === null) return

    const theme = themes[index]
    if (theme?.inUse) {
      console.debug('[DEBUG] handleSaveEditedTheme bloqueado para tema em uso', { index, theme })
      setDropDownIndex(null)
      return
    }

    const nameToSave = newNameInput.trim() || 'Tema'
    setThemes((prev) => prev.map((theme, idx) => (idx === index ? { ...theme, name: nameToSave, updated: true } : theme)))
  }

  const isChangingColorRef = useRef(false)
  const isDeletingThemeRef = useRef(false)

  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    console.debug('[DEBUG] handleInputBlur', { dropDownIndex, value: newNameInput, relatedTarget: event.relatedTarget, isChangingColor: isChangingColorRef.current, isDeletingTheme: isDeletingThemeRef.current })

    if (isChangingColorRef.current || isDeletingThemeRef.current) {
      console.debug('[DEBUG] blur ignorado devido a ação no dropdown (cor/excluir)')
      isChangingColorRef.current = false
      isDeletingThemeRef.current = false
      return
    }

    const relatedTarget = event.relatedTarget as HTMLElement | null
    if (relatedTarget && dropdownRef.current && dropdownRef.current.contains(relatedTarget)) {
      console.debug('[DEBUG] blur para elemento interno do dropdown, não fechar ainda')
      return
    }

    // Persist name edit when input is blurred
    handleSaveEditedTheme()
    // Close dropdown when blur happens (after saving)
    handleCloseDropdown()
  }

  const handleCloseDropdown = () => {
    console.debug('[DEBUG] handleCloseDropdown')
    setDropDownIndex(null)
    setDropdownCoords(null)
  }

  const handleSaveEditedThemeAndClose = () => {
    handleSaveEditedTheme()
    handleCloseDropdown()
  }

  const handleDeleteTheme = async () => {
    if (dropDownIndex === null) return

    const themeToRemove = themes[dropDownIndex]
    if (themeToRemove?.id) {
      try {
        await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/themes/${themeToRemove.id}`)
        setFeedback({
          isOpen: true,
          statusOk: true,
          message: 'Tema excluído com sucesso.',
        })
        setThemes((prev) => prev.filter((_, index) => index !== dropDownIndex))
      } catch (error: any) {
        console.error('Erro ao excluir tema:', error)
        setFeedback({
          isOpen: true,
          statusOk: false,
          message:
            error?.response?.data?.message ||
            'Erro ao excluir tema. Verifique se não está vinculado a família ou coleção.',
        })
      }
      setDropDownIndex(null)
      return
    }

    setThemes((prev) => prev.filter((_, index) => index !== dropDownIndex))
    setDropDownIndex(null)
  }

  const handleChangeColor = (index: number, color: string) => {
    console.debug('[DEBUG] handleChangeColor', { index, color, before: themes[index] })
    const normalizedColor = getHexColorOrDefault(color)
    setThemes((prev) => prev.map((theme, idx) => (idx === index ? { ...theme, color: normalizedColor, updated: true } : theme)))
  }

  const handleHexInputChange = (index: number, rawValue: string) => {
    isChangingColorRef.current = true
    setNewColorInput(rawValue)
    const normalizedColor = normalizeHexColor(rawValue)
    if (normalizedColor) {
      setThemes((prev) => prev.map((theme, idx) => (idx === index ? { ...theme, color: normalizedColor, updated: true } : theme)))
    }
  }

  const handleColorPickerChange = (index: number, color: string) => {
    const normalizedColor = getHexColorOrDefault(color)
    setNewColorInput(normalizedColor)
    handleChangeColor(index, normalizedColor)
  }

  const handleSaveThemes = async () => {
    console.debug('[DEBUG] handleSaveThemes', { themes })

    try {
      for (const theme of themes) {
        if (theme.id) {
          if (theme.updated) {
            await axios.put(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/themes/${theme.id}`, {
              name: theme.name,
              color: getHexColorOrDefault(theme.color),
            })
          }
        } else {
          const normalizedName = String(theme.name || '').trim()
          if (!normalizedName) {
            setFeedback({ isOpen: true, statusOk: false, message: 'Nome do tema é obrigatório.' })
            continue
          }

          await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/themes`, {
            name: normalizedName,
            color: getHexColorOrDefault(theme.color),
          })
        }
      }

      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product/themes`)
      const savedThemesRaw = Array.isArray(response.data) ? response.data : []
      const savedThemes = savedThemesRaw.map((theme: any) => ({
        ...theme,
        color: getHexColorOrDefault(theme.color),
      }))
      setThemes(savedThemes)
      onSave?.(savedThemes)
      closeModal()
    } catch (error: any) {
      console.error('[DEBUG] handleSaveThemes erro', error)
      let message = 'Erro ao salvar temas. Tente novamente.'

      if (error?.response?.status === 404) {
        message = 'Endpoint de temas não encontrado (404). Verifique se o backend-fashion-orders está atualizado.'
      } else if (error?.response?.data?.message) {
        message = error.response.data.message
      }

      setFeedback({
        isOpen: true,
        statusOk: false,
        message,
      })
    }
  }

  return (
    <ModalTest>
      <S.ContainerOfModal width='600px'>
        <S.Header>
          <S.HeaderTitle>Temas</S.HeaderTitle>
          <S.CloseDiv onClick={closeModal}> <X size={20} /></S.CloseDiv>
        </S.Header>
        <S.FormContainer onSubmit={(e) => e.preventDefault()}>
          {themes.map((theme, index) => (
            <S.InputDiv
              key={theme.id || `new-${index}`}
              ref={(el) => (rowRefs.current[index] = el)}
              style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', position: 'relative', overflow: 'visible' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                <ColorDiv color={getHexColorOrDefault(theme.color)} />
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '4px 10px',
                    borderRadius: 999,
                    fontSize: 13,
                    fontWeight: 500,
                    backgroundColor: getHexColorOrDefault(theme.color),
                    color: getTagTextColor(getHexColorOrDefault(theme.color)),
                  }}
                >
                  {theme.name}
                  {theme.inUse && (
                    <span style={{ fontSize: 12, opacity: 0.92 }}>(em uso)</span>
                  )}
                </span>
              </div>
              <Ellipsis size={20} style={{ cursor: 'pointer' }} color='#696969' onClick={() => handleIsEditingTheme(index)} />
              {dropDownIndex === index && dropdownCoords && (
                <div
                  ref={dropdownRef}
                  style={{
                    position: 'fixed',
                    top: dropdownCoords.top,
                    left: dropdownCoords.left,
                    width: dropdownCoords.width,
                    backgroundColor: 'white',
                    padding: '.8rem',
                    borderRadius: '5px',
                    zIndex: 9999,
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
                    overflow: 'visible',
                    whiteSpace: 'normal',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <S.Input
                      placeholder='Digite aqui'
                      value={newNameInput}
                      onChange={(e) => setNewNameInput(e.target.value)}
                      disabled={dropDownIndex !== null && themes[dropDownIndex]?.inUse}
                      style={{ opacity: dropDownIndex !== null && themes[dropDownIndex]?.inUse ? 0.6 : 1 }}
                      autoFocus={!(dropDownIndex !== null && themes[dropDownIndex]?.inUse)}
                      onBlur={handleInputBlur}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleSaveEditedThemeAndClose()
                        }
                      }}
                    />
                    <div
                      style={{ marginTop: '8px', cursor: 'pointer' }}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        isDeletingThemeRef.current = true
                      }}
                      onClick={handleDeleteTheme}
                    >
                      <Trash size={20} color='#BF383B' />
                      <span style={{ fontSize: '15px', fontWeight: 400, color: '#BF383B' }}>Excluir</span>
                    </div>
                    <S.InputDiv style={{ gap: '.5rem' }}>
                      <S.Label style={{ fontWeight: 500 }}>Cor (HEX)</S.Label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
                        <ColorDiv color={getHexColorOrDefault(themes[index]?.color)} />
                        <input
                          type='color'
                          value={getHexColorOrDefault(themes[index]?.color)}
                          onMouseDown={(e) => {
                            e.preventDefault()
                            isChangingColorRef.current = true
                          }}
                          onChange={(event) => handleColorPickerChange(index, event.target.value)}
                          style={{ width: '40px', height: '32px', border: 'none', padding: 0, background: 'transparent', cursor: 'pointer' }}
                        />
                        <S.Input
                          placeholder='#AABBCC'
                          value={newColorInput}
                          onMouseDown={() => {
                            isChangingColorRef.current = true
                          }}
                          onChange={(event) => handleHexInputChange(index, event.target.value)}
                        />
                      </div>
                    </S.InputDiv>
                  </div>
                </div>
              )}
            </S.InputDiv>
          ))}
          <AddInStep type='button' onClick={() => setThemes([...themes, { name: 'Tema', color: DEFAULT_HEX_COLOR }])}><Plus />Criar novo tema</AddInStep>
        </S.FormContainer>
        <S.BottomDiv>
          <S.ButtonContainer>
            <S.Button type='button' onClick={closeModal} className='cancel'>Cancelar</S.Button>
            <S.Button type='button' onClick={handleSaveThemes}>Salvar e fechar</S.Button>
          </S.ButtonContainer>
        </S.BottomDiv>
      </S.ContainerOfModal>
    </ModalTest>
  )
}

export default ViewOrEditThemes
